import { atom } from "jotai"

export type TableContext = {
  id: string
  name: string
  /**
   * PDV/POS external code for the table, when the merchant has one configured.
   * Optional and additive: entries persisted before this field existed simply
   * come back as `undefined` (JSON.parse doesn't reject missing keys), so old
   * sessionStorage entries rehydrate fine without a migration.
   */
  externalCode?: string
}

const TABLE_SESSION_KEY = "meucontroleai:table"

function readFromSession(): TableContext | null {
  try {
    const raw = sessionStorage.getItem(TABLE_SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as TableContext
  } catch {
    return null
  }
}

/**
 * Holds the resolved table context when the customer arrived via a
 * QR-code URL (?table=<uuid>). Null means normal delivery/takeout mode.
 *
 * The atom is initialised from sessionStorage so it survives page
 * refreshes inside the menu → cart → checkout flow.
 */
export const tableAtom = atom<TableContext | null>(readFromSession())

/**
 * Writes the table context to sessionStorage.
 * Call this whenever tableAtom is updated so the value persists across
 * navigation (sessionStorage survives React Router soft-navigations and
 * F5 reloads but is cleared when the tab is closed).
 */
export function persistTableContext(ctx: TableContext | null): void {
  if (ctx) {
    sessionStorage.setItem(TABLE_SESSION_KEY, JSON.stringify(ctx))
  } else {
    sessionStorage.removeItem(TABLE_SESSION_KEY)
  }
}

/**
 * Reads the stored table context from sessionStorage without touching
 * the atom. Useful in non-React contexts (e.g. inside plain functions).
 */
export function getStoredTable(): TableContext | null {
  return readFromSession()
}
